import { createContext, useContext } from 'react'
import type {
  EntityDef,
  MappingColumn,
  TenantSettings,
  UploadResult,
  ValidationReport,
} from '../api/types'

export interface ImportDraft {
  entityType: string
  entity: EntityDef | null
  upload: UploadResult | null
  sheet: string | null
  columns: MappingColumn[]
  unmapped: string[]
  validation: ValidationReport | null
  saveMappingAs: string
  /**
   * Options carried by a saved mapping the user picked. Kept so reusing a
   * template does not silently drop the knobs it was saved with; the tenant's
   * current settings still win over them, because a template is a column layout
   * and the Settings page is where the workspace's behaviour is decided.
   */
  options: Record<string, string> | null
}

export interface ImportWizard extends ImportDraft {
  entities: EntityDef[]
  /**
   * The tenant's saved import preferences, or null until they load.
   *
   * Held here rather than fetched per page because step 4 needs them to fold
   * into the mapping it validates and submits, and the Settings modal in that
   * same step writes them back — one copy in one place is what keeps the
   * preview and the submitted job describing the same run.
   */
  settings: TenantSettings | null
  settingsError: string | null
  reloadSettings: () => void
  setEntityType: (name: string) => void
  setUpload: (upload: UploadResult | null) => void
  setSheet: (sheet: string) => void
  setColumns: (columns: MappingColumn[]) => void
  setUnmapped: (unmapped: string[]) => void
  setValidation: (report: ValidationReport | null) => void
  setSaveMappingAs: (name: string) => void
  setOptions: (options: Record<string, string> | null) => void
  reset: () => void
}

export const ImportWizardContext = createContext<ImportWizard | null>(null)

export function useImportWizard(): ImportWizard {
  const context = useContext(ImportWizardContext)
  if (!context) throw new Error('useImportWizard must be used inside the import route')
  return context
}
